/**
 * Configuration for user identification across different applications
 * Each app can have its own API endpoint and data transformation logic
 */

import {
	getAppIdentPattern,
} from './urlPatterns';

export interface AppIdentConfig {
	/**
	 * The API endpoint to fetch user data
	 */
	apiUrl?: string;

	/**
	 * Transform the raw API response to extract user data
	 * @param response The raw response from the API
	 * @returns The extracted user data
	 */
	extractUserData?: (response: any) => any;

	/**
	 * Validate that the user data contains required fields
	 * @param userData The user data to validate
	 * @returns true if the data is valid for identification
	 */
	validateUserData?: (userData: any) => boolean;

	/**
	 * Extract organization from user data
	 * @param userData The user data to extract organization from
	 * @returns The organization code or undefined
	 */
	extractOrganization: (userData: any) => string | undefined;

	/**
	 * Extract user groups from user data, grouped by organization code
	 * Only available on configs that use the OIDC /users/v1/me API (default, keep)
	 * @param userData The user data to extract groups from
	 * @returns Map of organization code to group labels
	 */
	extractGroups?: (userData: any) => Record<string, string[]>;
}

/**
 * Extract group labels from an organizations array, grouped by organization code.
 * Returns a map of { orgCode: [groupLabels] } for all orgs that have groups.
 */
function extractGroupsByOrg(orgs: any[] | undefined): Record<string, string[]> {
	if (!orgs || orgs.length === 0) return {};
	const result: Record<string, string[]> = {};
	for (const org of orgs) {
		if (!org?.code) continue;
		const labels = (org.groups || [])
			.map((g: any) => g?.label)
			.filter((label: any): label is string => typeof label === 'string');
		if (labels.length > 0) result[org.code] = labels;
	}
	return result;
}

/**
 * Configuration for each application
 * The 'default' config is used as fallback for unknown apps
 */
export const APP_IDENT_CONFIGS: Record<string, AppIdentConfig> = {
	// AbylaWeb returns user data wrapped in a "user" object
	// Format: {"user": {"id": "...", "organization": "HHA", ...}}
	'abylaweb': {
		apiUrl: getAppIdentPattern('ABYLAWEB'),
		extractOrganization: (userData) => userData?.organization,
		extractUserData: (response) => response?.user || response,
		validateUserData: (userData) => !!(userData?.id),
	},

	// Default configuration for most apps
	// Format: {"uid": "...", "username": "...", ...}
	'default': {
		apiUrl: getAppIdentPattern('DEFAULT'),
		extractGroups: (userData) => {
			const orgs = userData?.authorities?.[0]?.organizations || userData?.organizations;
			return extractGroupsByOrg(orgs);
		},
		extractOrganization: (userData) => {
			// Try authorities format first (newer format), then direct organizations (legacy)
			const orgs = userData?.authorities?.[0]?.organizations || userData?.organizations;
			if (!orgs || orgs.length === 0) return undefined;
			if (orgs.length === 1) return orgs[0].code;
			return 'STONALINT';
		},
		extractUserData: (response) => response,
		validateUserData: (userData) => !!(userData?.uid || userData?.id),
	},

	// Keep returns flat user data with authorities
	// Format: {"id": "3", "authorities": [{"organizations": [...]}], ...}
	'keep': {
		apiUrl: getAppIdentPattern('KEEP'),
		extractGroups: (userData) => extractGroupsByOrg(userData?.authorities?.[0]?.organizations),
		extractOrganization: (userData) => {
			const orgs = userData?.authorities?.[0]?.organizations;
			if (!orgs || orgs.length === 0) return undefined;
			if (orgs.length === 1) return orgs[0].code;
			return 'STONALINT';
		},
		extractUserData: (response) => response,
		validateUserData: (userData) => !!(userData?.id),
	},

	// Model uses AbylaWeb's API structure
	// Format: {"user": {"id": "...", "organization": "...", ...}}
	'model': {
		apiUrl: getAppIdentPattern('MODEL'),
		extractOrganization: (userData) => userData?.organization,
		extractUserData: (response) => response?.user || response,
		validateUserData: (userData) => !!(userData?.id),
	},
};

/**
 * Get the configuration for a specific app
 * @param appName The name of the application
 * @returns The configuration for the app, or the default config if not found
 */
export function getAppIdentConfig(appName: string | undefined): AppIdentConfig {
	if (!appName) {
		return APP_IDENT_CONFIGS['default'];
	}

	return APP_IDENT_CONFIGS[appName] || APP_IDENT_CONFIGS['default'];
}
